<template>
    <Teleport to="body">
        <Transition name="dv2-modal">
            <div
                v-if="open"
                class="dv2-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="dv2-promo-title"
                @click.self="dismissSession"
            >
                <div class="dv2-modal__card">
                    <button type="button" class="dv2-modal__close" aria-label="Fechar" @click="dismissSession">
                        ×
                    </button>

                    <div class="dv2-modal__hero" aria-hidden="true">
                        <div class="dv2-modal__orb dv2-modal__orb--a"></div>
                        <div class="dv2-modal__orb dv2-modal__orb--b"></div>
                        <div class="dv2-modal__badge">Novo</div>
                        <div class="dv2-modal__spark">✦</div>
                    </div>

                    <div class="dv2-modal__body">
                        <h2 id="dv2-promo-title">Conheça o novo Dashboard</h2>
                        <p>
                            Uma nova experiência foi criada para deixar seu dia a dia mais simples, rápido e organizado, com fila de correções, atalhos e um assistente ao seu lado.
                        </p>

                        <ul class="dv2-modal__features">
                            <li>Priorize correções perto do prazo</li>
                            <li>Veja a fila e o destaque em um olhar</li>
                            <li>Pergunte ao assistente o que precisa agora</li>
                        </ul>

                        <label class="dv2-modal__never">
                            <input v-model="neverAgain" type="checkbox" />
                            <span>Não mostrar novamente</span>
                        </label>

                        <div class="dv2-modal__actions">
                            <button
                                type="button"
                                class="dv2-modal__btn dv2-modal__btn--ghost"
                                @click="dismissWithChoice"
                            >
                                Agora não
                            </button>
                            <button
                                type="button"
                                class="dv2-modal__btn dv2-modal__btn--primary"
                                :disabled="prefs.saving"
                                @click="experimentar"
                            >
                                Experimentar o novo dashboard
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useUserDashboardPreferencesStore } from '@/stores/userDashboardPreferences';
import { switchToNewDashboardExperience } from '@/utils/dashboardV2Navigation';

const router = useRouter();
const prefs = useUserDashboardPreferencesStore();
const neverAgain = ref(false);
const ready = ref(false);

const open = computed(
    () =>
        ready.value &&
        prefs.shouldShowTryPromo &&
        !prefs.isPromoHiddenPermanently &&
        !prefs.isPromoDismissedThisSession
);

onMounted(async () => {
    await prefs.fetchPreferences();
    ready.value = true;
    document.addEventListener('keydown', onKeydown);
});

onUnmounted(() => {
    document.removeEventListener('keydown', onKeydown);
});

function onKeydown(e) {
    if (e.key === 'Escape' && open.value) {
        dismissSession();
    }
}

async function experimentar() {
    try {
        await switchToNewDashboardExperience(prefs, router);
    } catch {
        /* store */
    }
}

async function dismissWithChoice() {
    if (neverAgain.value) {
        await prefs.hidePromoPermanently();
    } else {
        prefs.dismissPromoThisSession();
    }
}

function dismissSession() {
    prefs.dismissPromoThisSession();
}
</script>

<style scoped>
.dv2-modal {
    position: fixed;
    inset: 0;
    z-index: 10050;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    background: rgba(15, 23, 42, 0.48);
    backdrop-filter: blur(6px);
}

.dv2-modal__card {
    position: relative;
    width: min(480px, 100%);
    background: #fff;
    border-radius: 22px;
    overflow: hidden;
    box-shadow: 0 24px 64px rgba(15, 23, 42, 0.28);
}

.dv2-modal__close {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 2;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.7);
    color: #5b6b82;
    font-size: 22px;
    line-height: 1;
    cursor: pointer;
}

.dv2-modal__close:hover {
    background: #fff;
    color: #1a2b4c;
}

.dv2-modal__hero {
    position: relative;
    height: 140px;
    background: linear-gradient(135deg, #2f6fed 0%, #6ea0ff 48%, #c7b8ff 100%);
    overflow: hidden;
}

.dv2-modal__orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(2px);
}

.dv2-modal__orb--a {
    width: 160px;
    height: 160px;
    background: rgba(255, 255, 255, 0.22);
    top: -40px;
    left: -30px;
}

.dv2-modal__orb--b {
    width: 120px;
    height: 120px;
    background: rgba(255, 255, 255, 0.16);
    right: 24px;
    bottom: -36px;
}

.dv2-modal__badge {
    position: absolute;
    top: 20px;
    left: 22px;
    padding: 6px 12px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.22);
    color: #fff;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.dv2-modal__spark {
    position: absolute;
    right: 28px;
    top: 36px;
    font-size: 42px;
    color: rgba(255, 255, 255, 0.92);
    text-shadow: 0 8px 24px rgba(15, 23, 42, 0.2);
}

.dv2-modal__body {
    padding: 24px 26px 26px;
}

.dv2-modal__body h2 {
    margin: 0 0 10px;
    font-size: 24px;
    font-weight: 800;
    color: #1a2b4c;
    letter-spacing: -0.02em;
}

.dv2-modal__body > p {
    margin: 0 0 16px;
    font-size: 14px;
    line-height: 1.5;
    color: #5b6b82;
}

.dv2-modal__features {
    margin: 0 0 18px;
    padding: 0;
    list-style: none;
    display: grid;
    gap: 8px;
}

.dv2-modal__features li {
    position: relative;
    padding-left: 22px;
    font-size: 13px;
    font-weight: 600;
    color: #1a2b4c;
}

.dv2-modal__features li::before {
    content: '';
    position: absolute;
    left: 0;
    top: 6px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #2f6fed;
}

.dv2-modal__never {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 18px;
    font-size: 13px;
    color: #5b6b82;
    cursor: pointer;
    user-select: none;
}

.dv2-modal__never input {
    width: 16px;
    height: 16px;
    accent-color: #2f6fed;
}

.dv2-modal__actions {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    flex-wrap: wrap;
}

.dv2-modal__btn {
    border-radius: 12px;
    padding: 11px 16px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    border: 1px solid transparent;
}

.dv2-modal__btn--primary {
    background: #2f6fed;
    color: #fff;
}

.dv2-modal__btn--primary:hover {
    background: #2159d6;
}

.dv2-modal__btn--primary:disabled {
    opacity: 0.7;
    cursor: wait;
}

.dv2-modal__btn--ghost {
    background: transparent;
    border-color: #d7deea;
    color: #5b6b82;
}

.dv2-modal__btn--ghost:hover {
    background: #f5f7fb;
    color: #1a2b4c;
}

.dv2-modal-enter-active,
.dv2-modal-leave-active {
    transition: opacity 0.2s ease;
}

.dv2-modal-enter-active .dv2-modal__card,
.dv2-modal-leave-active .dv2-modal__card {
    transition: transform 0.22s ease, opacity 0.22s ease;
}

.dv2-modal-enter-from,
.dv2-modal-leave-to {
    opacity: 0;
}

.dv2-modal-enter-from .dv2-modal__card,
.dv2-modal-leave-to .dv2-modal__card {
    opacity: 0;
    transform: translateY(12px) scale(0.98);
}

@media (max-width: 520px) {
    .dv2-modal__actions {
        flex-direction: column-reverse;
    }

    .dv2-modal__btn {
        width: 100%;
    }
}
</style>
